import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateUserProfileSchema } from '@shared/validation/schemas';
import { UpdateUserProfileData } from '@shared/types';
import { useAuthListener } from '../hooks/useAuthListener';
import { auth, db } from '../firebase/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { Header } from '../components/layout/Header';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

export function UserProfilePage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [aliasChecking, setAliasChecking] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { user, userProfile, updateUserProfile } = useAuthListener(auth, db);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<UpdateUserProfileData>({
    resolver: zodResolver(UpdateUserProfileSchema),
    defaultValues: {
      phone: userProfile?.phone || '',
      alias: userProfile?.alias || '',
    },
  });

  // Simple alias validation - only check on save
  const checkAliasUniqueness = async (alias: string, userId: string): Promise<boolean> => {
    if (!alias || alias.trim() === '') return true; // Empty aliases are allowed
    
    // Don't check if it's the same as current alias
    if (alias === userProfile?.alias) return true;
    
    setAliasChecking(true);
    try {
      // Check if alias is unique using direct Firebase calls
      const usersRef = collection(db, 'users');
      const aliasQuery = query(usersRef, where('alias', '==', alias));
      const snapshot = await getDocs(aliasQuery);
      
      // Filter out the current user's own alias
      const isUnique = snapshot.docs.every(doc => doc.id !== userId);
      return isUnique;
    } catch {
      return true; // Allow on error to not block user
    } finally {
      setAliasChecking(false);
    }
  };

  // Update form when userProfile changes
  useEffect(() => {
    if (userProfile) {
      reset({
        phone: userProfile.phone || '',
        alias: userProfile.alias || '',
      });
    }
  }, [userProfile, reset]);

  const onSubmit = async (data: UpdateUserProfileData) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);


      // Check alias uniqueness if alias is being changed
      if (data.alias && data.alias !== userProfile?.alias) {
        const isUnique = await checkAliasUniqueness(data.alias, user.uid);
        if (!isUnique) {
          setError('This poker alias is already taken. Please choose a different one.');
          setLoading(false);
          return;
        }
      }

      // Only update fields that have changed
      const updates: Partial<UpdateUserProfileData> = {};
      if (data.phone !== userProfile?.phone) updates.phone = data.phone;
      if (data.alias !== userProfile?.alias) updates.alias = data.alias;

      // Filter out undefined values
      const filteredUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value !== undefined)
      );


      if (Object.keys(filteredUpdates).length > 0) {
        await updateUserProfile(filteredUpdates);
        setSuccess('Profile updated successfully!');
        reset(data); // Reset form to mark as not dirty
      } else {
        setSuccess('No changes to save');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const firstConfirm = confirm(
      'Are you sure you want to delete your account? This action will immediately disable your account and you will be signed out. This action cannot be undone.'
    );
    
    if (!firstConfirm) return;

    const secondConfirm = confirm(
      'This is your last chance to cancel. Your account will be permanently disabled and you will lose access to all features. Are you absolutely sure?'
    );
    
    if (!secondConfirm) return;

    try {
      setDeleteLoading(true);
      setError(null);
      
      const requestAccountDeletion = httpsCallable(functions, 'requestAccountDeletion');
      await requestAccountDeletion();
      
      // Sign out and redirect to home
      await signOut(auth);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!user || !userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <Header userProfile={userProfile} />
        
        <div className="max-w-2xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
              <p className="mt-1 text-sm text-gray-600">
                Update your personal information and preferences
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-6 space-y-6">
              {/* Account Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={userProfile.email}
                      disabled
                      className="input bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Email cannot be changed. Contact support if needed.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="name" className="label">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={userProfile.name}
                      disabled
                      className="input bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Name cannot be changed after signup. Contact support if needed.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="phone" className="label">
                      Cell Phone
                    </label>
                    <input
                      {...register('phone')}
                      type="tel"
                      className={`input ${errors.phone ? 'border-red-500' : ''}`}
                      placeholder="Enter your cell phone number"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Entering your number opts you in for poker waitlist & promo texts. Msg/data rates may apply.
                    </p>
                  </div>
                </div>
              </div>

              {/* Poker Preferences */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Poker Preferences</h3>
                
                <div>
                  <label htmlFor="alias" className="label">
                    Poker Alias
                  </label>
                  <div className="relative">
                    <input
                      {...register('alias')}
                      type="text"
                      maxLength={12}
                      className={`input ${errors.alias ? 'border-red-500' : ''} ${aliasChecking ? 'pr-10' : ''}`}
                      placeholder="Enter your poker alias (max 12 characters)"
                    />
                    {aliasChecking && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <LoadingSpinner size="sm" />
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    This name will be displayed on waitlists. If left blank, your full name will be used.
                  </p>
                  {errors.alias && (
                    <p className="mt-1 text-sm text-red-600">{errors.alias.message}</p>
                  )}
                </div>
              </div>

              {/* Account Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Details</h3>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Role:</span>
                    <span className="capitalize font-medium">{userProfile.role}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Member since:</span>
                    <span>{userProfile.created_at?.toDate().toLocaleDateString()}</span>
                  </div>
                  {userProfile.last_active && (
                    <div className="flex justify-between">
                      <span>Last active:</span>
                      <span>{userProfile.last_active.toDate().toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Danger Zone */}
              <div className="border-t border-red-200 pt-6">
                <h3 className="text-lg font-medium text-red-900 mb-4">Danger Zone</h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-red-900">Delete Account</h4>
                      <p className="text-sm text-red-700 mt-1">
                        Permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDeleteAccount}
                      disabled={deleteLoading}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      {deleteLoading ? (
                        <LoadingSpinner size="sm" className="mr-2" />
                      ) : null}
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {success && (
                <div className="rounded-md bg-green-50 p-4">
                  <p className="text-sm text-green-800">{success}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => reset()}
                  disabled={!isDirty || loading}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || aliasChecking || !isDirty}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : null}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
